import React from 'react';
import { LifeBuoy, MessageCircle, ExternalLink, Mail } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { useHelpCenter } from 'components/context/HelpCenterContext';
import { logApi } from 'api';

const { createLog } = logApi;

/** Mismo número base que WhatsAppButton (sin + ni espacios). */
const DEFAULT_SUPPORT_WHATSAPP = '59896964204';

const DEFAULT_SUPPORT_EMAIL = 'fran@espor.ai';

const SupportTab = () => {
  const { openHelpCenter } = useHelpCenter();
  const waNumber = (process.env.REACT_APP_SUPPORT_WHATSAPP || DEFAULT_SUPPORT_WHATSAPP).replace(/\D/g, '');
  const whatsappHref = `https://wa.me/${waNumber}?text=${encodeURIComponent('Hola, necesito ayuda con espor.ai')}`;
  const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || DEFAULT_SUPPORT_EMAIL;
  const mailtoHref = `mailto:${supportEmail}?subject=${encodeURIComponent('Consulta espor.ai')}`;

  const handleOpenHelp = () => {
    createLog({ level: 'info', message: 'Click en "Abrir centro de ayuda" desde perfil Soporte' });
    openHelpCenter();
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/15 shadow-[0_24px_80px_-24px_rgba(15,23,42,0.9)] backdrop-blur-xl">
      <div
        className="pointer-events-none absolute inset-0 z-0"
        aria-hidden
        style={{
          backgroundImage: `
            radial-gradient(ellipse 90% 70% at 100% 0%, rgba(139, 92, 246, 0.18), transparent 52%),
            radial-gradient(ellipse 70% 55% at 0% 100%, rgba(59, 130, 246, 0.14), transparent 48%),
            linear-gradient(165deg, rgba(30, 41, 59, 0.92) 0%, rgba(15, 23, 42, 0.88) 45%, rgba(30, 27, 75, 0.75) 100%)
          `,
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.12] [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:40px_40px]"
        aria-hidden
      />
      <div className="relative z-10 p-4 md:p-8">
        <div className="border-b border-white/10 pb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/50 to-indigo-600/40 shadow-inner ring-1 ring-white/15 backdrop-blur-md">
            <LifeBuoy className="h-5 w-5 text-white" aria-hidden />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Soporte</h3>
            <p className="text-white/70 text-sm">
              Guías, preguntas frecuentes y contacto con el equipo de espor.ai.
            </p>
          </div>
        </div>
        </div>

        <div className="space-y-8 pt-6 md:pt-8">
        <section className="space-y-4">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-white/85">
            Centro de ayuda
          </h4>
          <div className="rounded-xl border border-white/10 bg-white/[0.06] p-4 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] backdrop-blur-sm md:p-5">
            <p className="text-white/70 text-sm md:text-base mb-4">
              Encontrá artículos sobre la plataforma, conexiones con Google y Meta, objetivos,
              optimizaciones y más.
            </p>
            <button
              type="button"
              onClick={handleOpenHelp}
              className="inline-flex items-center gap-2 rounded-xl px-5 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-sm font-medium shadow-lg transition-all"
            >
              <MessageCircle className="h-4 w-4" aria-hidden />
              Abrir centro de ayuda
            </button>
          </div>
        </section>

        <section className="space-y-4 border-t border-white/10 pt-2">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-white/85">
            Correo
          </h4>
          <div className="rounded-xl border border-white/10 bg-white/[0.06] p-4 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] backdrop-blur-sm md:p-5">
            <p className="text-white/70 text-sm md:text-base mb-4">
              Podés escribirnos por correo y te respondemos a la brevedad.
            </p>
            <a
              href={mailtoHref}
              onClick={() =>
                createLog({ level: 'info', message: 'Click en correo soporte desde perfil' })
              }
              className="inline-flex items-center gap-2 rounded-xl px-5 py-3 border border-sky-500/40 bg-sky-600/20 hover:bg-sky-600/30 text-sky-100 text-sm font-medium transition-colors"
            >
              <Mail className="h-5 w-5" aria-hidden />
              Enviar mail a {supportEmail}
            </a>
          </div>
        </section>

        <section className="space-y-4 border-t border-white/10 pt-2">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-white/85">
            WhatsApp
          </h4>
          <div className="rounded-xl border border-white/10 bg-white/[0.06] p-4 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] backdrop-blur-sm md:p-5">
            <p className="text-white/70 text-sm md:text-base mb-4">
              Escribinos por WhatsApp para consultas o incidencias.
            </p>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                createLog({ level: 'info', message: 'Click en WhatsApp soporte desde perfil' })
              }
              className="inline-flex items-center gap-2 rounded-xl px-5 py-3 border border-emerald-500/40 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-100 text-sm font-medium transition-colors"
            >
              <FaWhatsapp className="h-5 w-5" aria-hidden />
              Chatear por WhatsApp
              <ExternalLink className="h-3.5 w-3.5 opacity-70" aria-hidden />
            </a>
          </div>
        </section>
        </div>
      </div>
    </div>
  );
};

export default SupportTab;
